import { useEffect, useState } from 'react';
import apiService from '../services/api';
import { AdminUser } from '../types';

interface AuthUser {
  id: number;
  username: string;
  name: string;
  mobile: string;
  email?: string;
  role: string;
  roles: string[];
  technicianIds: number[];
}

const mapAdminUser = (user: AdminUser): AuthUser => ({
  id: user.id,
  username: user.name,
  name: user.name,
  mobile: user.mobile,
  email: user.email,
  role: user.roles?.[0] || '',
  roles: user.roles || [],
  technicianIds: user.technicianIds || [],
});

const getStoredUser = (): AuthUser | null => {
  const token = localStorage.getItem('token');
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const userData = localStorage.getItem('user');

  if (!token || !isLoggedIn || !userData) {
    return null;
  }

  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const storedUser = getStoredUser();
  const [user, setUser] = useState<AuthUser | null>(storedUser);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!storedUser);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const userData = localStorage.getItem('user');

    if (token && isLoggedIn && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    } else {
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  };

  const sendOtp = async (mobile: string): Promise<boolean> => {
    try {
      const response = await apiService.sendAdminOtp(mobile);
      return response.isSuccess;
    } catch (error) {
      console.error('Send OTP error:', error);
      return false;
    }
  };

  const verifyOtp = async (mobile: string, otp: string): Promise<boolean> => {
    try {
      const response = await apiService.verifyAdminOtp(mobile, otp);
      const mappedUser = mapAdminUser(response.user);

      localStorage.setItem('token', response.token);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('user', JSON.stringify(mappedUser));
      localStorage.setItem('tokenExpiresAt', response.expiresAt);

      setUser(mappedUser);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Verify OTP error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiresAt');
    setUser(null);
    setIsAuthenticated(false);
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    sendOtp,
    verifyOtp,
    logout,
    checkAuthStatus
  };
};
